/**
 * Component Registry
 *
 * Maps functionId to lazy-loaded components.
 * This is the ONLY place where you need to register new features.
 *
 * Convention:
 * - functionId: UPPERCASE (matches MFUN_TBL)
 * - component: Lazy-loaded feature component
 * - path: Auto-generated from functionId (lowercase)
 *
 * @example
 * ```ts
 * // Backend DB: MFUN_FUNID = 'SUPPLIER'
 * // Just add here:
 * {
 *   functionId: 'SUPPLIER',
 *   component: lazy(() => import('@/features/supplier-management'))
 * }
 * // That's it! No need to modify routes.tsx
 * ```
 */
import { lazy, type ComponentType } from 'react';

const BREADCRUMB_HOME = 'Home';
const FX_ENTRY_TITLE = 'FX Entry';
const FX_ENTRY_BREADCRUMB = [BREADCRUMB_HOME, 'Forex', FX_ENTRY_TITLE];
const FX_ENTRY_COMPONENT = lazy(() =>
  import('@/features/fx-entry').then(m => ({
    default: m.FxEntry,
  })),
);

const normalizeFunctionId = (value: string): string => value.toUpperCase();

const isSameFunctionId = (left: string, right: string): boolean =>
  normalizeFunctionId(left) === normalizeFunctionId(right);

export interface ComponentRegistryItem {
  /** Function ID from MFUN_TBL (UPPERCASE) */
  functionId: string;
  /** Lazy-loaded component */
  component: ComponentType;
  /** Optional: Override default title */
  title?: string;
  /** Optional: Custom breadcrumb */
  breadcrumb?: string[];
}

/**
 * Register all feature components here
 * This is the single source of truth for feature → component mapping
 */
export const COMPONENT_REGISTRY: ComponentRegistryItem[] = [
  {
    functionId: 'DASHBOARD',
    component: lazy(() =>
      import('@/features/dashboard').then(m => ({ default: m.Dashboard })),
    ),
    title: 'Dashboard',
    breadcrumb: [BREADCRUMB_HOME, 'Dashboard'],
  },
  {
    functionId: 'USRMNT', // User Maintenance (MFUN_NODEID: 310)
    component: lazy(() =>
      import('@/features/user-management').then(m => ({
        default: m.UserManagement,
      })),
    ),
    title: 'User Maintenance',
    breadcrumb: [BREADCRUMB_HOME, 'User Management', 'User Maintenance'],
  },
  {
    functionId: 'USRPERM', // User Permission (Role Management) (MFUN_NODEID: 320)
    component: lazy(() =>
      import('@/features/role-management').then(m => ({
        default: m.RoleManagement,
      })),
    ),
    title: 'User Permission',
    breadcrumb: [BREADCRUMB_HOME, 'User Management', 'User Permission'],
  },
  {
    functionId: 'FXPOSRPT', // FX Position Report (MFUN_NODEID: 610)
    component: lazy(() =>
      import('@/features/fx-position').then(m => ({
        default: m.FxPositionReport,
      })),
    ),
    title: 'FX Position Report',
    breadcrumb: [BREADCRUMB_HOME, 'FX Position Report'],
  },
  {
    functionId: 'FXENTRY', // FX Entry (MFUN_NODEID: 610)
    component: FX_ENTRY_COMPONENT,
    title: FX_ENTRY_TITLE,
    breadcrumb: FX_ENTRY_BREADCRUMB,
  },
  {
    // Backward-compatibility alias for legacy functionId used in some environments.
    functionId: 'FXCNTRACT',
    component: FX_ENTRY_COMPONENT,
    title: FX_ENTRY_TITLE,
    breadcrumb: FX_ENTRY_BREADCRUMB,
  },
  {
    functionId: 'FXREQUEST', // FX Request (MFUN_NODEID: 620)
    component: lazy(() =>
      import('@/features/fx-request').then(m => ({
        default: m.FxRequest,
      })),
    ),
    title: 'FX Request',
    breadcrumb: [BREADCRUMB_HOME, 'Forex', 'FX Request'],
  },
  {
    functionId: 'FXCRPT01', // FX Balance Report (FXC_REPORT_01)
    component: lazy(() =>
      import('@/features/reports').then(m => ({
        default: m.FxBalanceReport,
      })),
    ),
    title: 'Balance Report',
    breadcrumb: [BREADCRUMB_HOME, 'FX Contract', 'Balance Report'],
  },
  {
    functionId: 'FXCRPT02', // FX Linkage Report by Contract (FXC_REPORT_02)
    component: lazy(() =>
      import('@/features/reports').then(m => ({
        default: m.FxLinkageByContractReport,
      })),
    ),
    title: 'Linkage Report by Contract',
    breadcrumb: [BREADCRUMB_HOME, 'FX Contract', 'Linkage Report by Contract'],
  },
  {
    functionId: 'FXCRPT03', // FX Linkage Report by Cus/Sup (FXC_REPORT_03)
    component: lazy(() =>
      import('@/features/reports').then(m => ({
        default: m.FxLinkageByCusSupReport,
      })),
    ),
    title: 'Linkage Report by Cus/Sup',
    breadcrumb: [BREADCRUMB_HOME, 'FX Contract', 'Linkage Report by Cus/Sup'],
  },
  {
    functionId: 'FXCRPT04', // FX Linkage Report by BG/Txn (FXC_REPORT_04)
    component: lazy(() =>
      import('@/features/reports').then(m => ({
        default: m.FxLinkageByBgTransactionReport,
      })),
    ),
    title: 'Linkage Report by BG/Txn',
    breadcrumb: [BREADCRUMB_HOME, 'FX Contract', 'Linkage Report by BG/Txn'],
  },
  {
    functionId: 'INTCAL',
    component: lazy(() =>
      import('@/features/internal-interest-calculation').then(m => ({
        default: m.InternalInterestCalculation,
      })),
    ),
    title: 'Internal Interest Calculation',
    breadcrumb: [BREADCRUMB_HOME, 'Internal Interest Calculation'],
  },
  // {
  //   functionId: 'GLAPULLCOA', // Pull COA (MFUN_NODEID: 410)
  //   component: lazy(() =>
  //     import('@/features/gla-pull-coa').then(m => ({
  //       default: m.GlaPullCoa,
  //     })),
  //   ),
  //   title: 'Pull COA',
  //   breadcrumb: ['Home', 'GLA+ Pull', 'Pull COA'],
  // },
  // {
  //   functionId: 'GLAPULLBAL', // Pull Balance (MFUN_NODEID: 420)
  //   component: lazy(() =>
  //     import('@/features/gla-pull-balance').then(m => ({
  //       default: m.GlaPullBalance,
  //     })),
  //   ),
  //   title: 'Pull Balance',
  //   breadcrumb: ['Home', 'GLA+ Pull', 'Pull Balance'],
  // },
  // {
  //   functionId: 'GLAPUSHAP', // Push AP Entry (MFUN_NODEID: 510)
  //   component: lazy(() =>
  //     import('@/features/gla-push-ap').then(m => ({
  //       default: m.GlaPushAp,
  //     })),
  //   ),
  //   title: 'Push AP Entry',
  //   breadcrumb: ['Home', 'GLA+ Push', 'Push AP Entry'],
  // },
  // {
  //   functionId: 'GLAPUSHAR', // Push AR Entry (MFUN_NODEID: 520)
  //   component: lazy(() =>
  //     import('@/features/gla-push-ar').then(m => ({
  //       default: m.GlaPushAr,
  //     })),
  //   ),
  //   title: 'Push AR Entry',
  //   breadcrumb: ['Home', 'GLA+ Push', 'Push AR Entry'],
  // },
  // {
  //   functionId: 'GLAPUSHJL', // Push JL Entry (MFUN_NODEID: 530)
  //   component: lazy(() =>
  //     import('@/features/gla-push-jl').then(m => ({
  //       default: m.GlaPushJl,
  //     })),
  //   ),
  //   title: 'Push JL Entry',
  //   breadcrumb: ['Home', 'GLA+ Push', 'Push JL Entry'],
  // },
];

/**
 * Get component for a functionId
 */
export const getComponentForFunction = (
  functionId: string,
): ComponentType | null => {
  const entry = COMPONENT_REGISTRY.find(item =>
    isSameFunctionId(item.functionId, functionId),
  );
  return entry?.component ?? null;
};

/**
 * Get title for a functionId
 */
export const getTitleForFunction = (functionId: string): string | null => {
  const entry = COMPONENT_REGISTRY.find(item =>
    isSameFunctionId(item.functionId, functionId),
  );
  return entry?.title ?? null;
};

/**
 * Get breadcrumb for a functionId
 */
export const getBreadcrumbForFunction = (
  functionId: string,
): string[] | null => {
  const entry = COMPONENT_REGISTRY.find(item =>
    isSameFunctionId(item.functionId, functionId),
  );
  return entry?.breadcrumb ?? null;
};

/**
 * Check if a functionId has a registered component
 */
export const hasRegisteredComponent = (functionId: string): boolean => {
  return COMPONENT_REGISTRY.some(item =>
    isSameFunctionId(item.functionId, functionId),
  );
};
